/**
 * Demo vendor scoping.
 *
 * When a demo customer account (users/{uid}.demoVendorScope === "nyc-demo") is
 * signed in on web, EVERY vendor discovery/search surface — browse, explore,
 * home, category discovery, and all of Evie's vendor tools — must show ONLY
 * vendors tagged `serviceProviders.demoSeed === <scope>`. No mock (Israeli)
 * vendors, no other real vendors, and NO location filtering required.
 *
 * Implemented as module-level state set at auth (mirrors setActiveCurrency):
 * the web auth layer sets it from the user doc before first render; mobile
 * never calls the setter, so it stays null there and nothing is filtered.
 *
 * Server components must NOT rely on this (module state is shared across
 * requests) — it's a client-only ("use client") concern. Vendor discovery all
 * runs client-side, so that's fine.
 */

let activeDemoVendorScope: string | null = null;

export function setActiveDemoVendorScope(scope: string | null | undefined): void {
  activeDemoVendorScope = scope ? String(scope) : null;
}

export function getActiveDemoVendorScope(): string | null {
  return activeDemoVendorScope;
}

/**
 * Scope-resolution gate.
 *
 * `setActiveDemoVendorScope` is set by the web auth layer only AFTER an async
 * read of the user doc (onAuthStateChanged → getDoc → setter). Vendor-discovery
 * pages, however, fire their fetch on mount with no dependency on auth, so the
 * query can race the setter and win — returning EVERY vendor (incl. other demo
 * scopes) because no scope was active yet. That is the "why do I still see NYC
 * vendors" leak when the Israel demo customer cold-loads /browse.
 *
 * Fix: discovery fetches `await whenScopeResolved()` before querying. The web
 * auth layer calls `markScopeResolved()` once, in every auth branch (signed in
 * with/without a scope, signed out, error). Callers therefore never query
 * before the scope is known. A timeout fallback guarantees discovery never
 * hangs if the auth layer somehow never marks it (fail-open to unscoped rather
 * than an infinite spinner).
 */
let scopeResolved = false;
let resolveScopeReady: () => void;
const scopeReadyPromise: Promise<void> = new Promise((res) => {
  resolveScopeReady = res;
});

/** Called by the web auth layer once auth state (and thus scope) is settled. */
export function markScopeResolved(): void {
  if (!scopeResolved) {
    scopeResolved = true;
    resolveScopeReady();
  }
}

/**
 * Resolves once the active demo scope is known. No-op on the server (module
 * state is per-request there and discovery is client-only) and returns
 * immediately once already resolved. Falls open after `timeoutMs` so a missing
 * `markScopeResolved()` degrades to an unscoped fetch instead of a hang.
 */
export async function whenScopeResolved(timeoutMs = 4000): Promise<void> {
  if (scopeResolved || typeof window === "undefined") return;
  await Promise.race([
    scopeReadyPromise,
    new Promise<void>((res) => setTimeout(res, timeoutMs)),
  ]);
}

/**
 * True when a demo scope is active AND this vendor doc does NOT belong to it —
 * i.e. the vendor should be hidden from the current (demo) user. Returns false
 * whenever no scope is active, so non-demo users are never filtered.
 */
export function isHiddenByDemoScope(
  data: { demoSeed?: string } | null | undefined,
): boolean {
  if (!activeDemoVendorScope) return false;
  return !data || data.demoSeed !== activeDemoVendorScope;
}

/** True when any demo vendor scope is active (mock vendors should be skipped). */
export function demoVendorScopeActive(): boolean {
  return activeDemoVendorScope !== null;
}
